/**
 * useOfferCapUsage — per-offer CAP usage (GET /offer-caps, MD-1147): the
 * bank's cap limit + the tracker's last persisted count over the offer's
 * timeframe, keyed by tracker offer id. The backend scheduler recounts every
 * 5 min; refresh() recounts now and swaps the payload into every reader.
 * For Media Devoted | Created By James Alexandrov
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/services/api";
import type { OfferCapUsageResponse } from "../bank-stats.types";

export const offerCapUsageKeys = {
  all: ["bank-offer-cap-usage"] as const,
};

const fetchOfferCapUsage = async (
  refresh = false,
): Promise<OfferCapUsageResponse> => {
  const res = await api.get<{ data: OfferCapUsageResponse }>(
    "/offer-caps",
    { params: refresh ? { refresh: true } : {} },
  );
  return res.data.data;
};

export function useOfferCapUsage(enabled = true) {
  return useQuery({
    queryKey: offerCapUsageKeys.all,
    queryFn: () => fetchOfferCapUsage(),
    staleTime: 60_000,
    enabled,
  });
}

/** Recount now (bypasses the server cache), then update every reader. */
export function useRefreshOfferCapUsage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchOfferCapUsage(true),
    onSuccess: (data) => {
      queryClient.setQueryData(offerCapUsageKeys.all, data);
    },
  });
}
